import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Heart, Activity, FileText, Save, AlertCircle } from 'lucide-react';
import { TensionFormData } from '../../types';

interface TensionFormProps {
  onSubmit: (data: TensionFormData) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<TensionFormData>;
  initialDate?: Date;
}

const getTensionCategory = (systolic: number, diastolic: number) => {
  if (systolic < 120 && diastolic < 80) {
    return { category: 'Normal', color: '#22c55e', description: 'Presión arterial normal' };
  } else if (systolic >= 120 && systolic <= 129 && diastolic < 80) {
    return { category: 'Elevada', color: '#f59e0b', description: 'Presión arterial elevada' };
  } else if ((systolic >= 130 && systolic <= 139) || (diastolic >= 80 && diastolic <= 89)) {
    return { category: 'Hipertensión Grado 1', color: '#f97316', description: 'Hipertensión en etapa 1' };
  } else if (systolic >= 140 || diastolic >= 90) {
    return { category: 'Hipertensión Grado 2', color: '#ef4444', description: 'Hipertensión en etapa 2' };
  }
  return { category: 'Indeterminada', color: '#6b7280', description: 'Valores atípicos' };
};

export const TensionForm: React.FC<TensionFormProps> = ({
  onSubmit,
  onCancel,
  initialData = {},
  initialDate,
}) => {
  // Format a Date object to datetime-local input format
  const formatDateForInput = (date: Date, useCurrentTime: boolean = false): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const now = new Date();
    const hours = String(useCurrentTime ? now.getHours() : date.getHours()).padStart(2, '0');
    const minutes = String(useCurrentTime ? now.getMinutes() : date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Parse ISO date string from API to datetime-local format
  const parseApiDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return formatDateForInput(new Date(), true);
      return formatDateForInput(date, false);
    } catch {
      return formatDateForInput(new Date(), true);
    }
  };

  // Determine initial datetime value
  const getInitialDatetime = (): string => {
    if (initialData.date) {
      return parseApiDate(initialData.date);
    }
    return formatDateForInput(initialDate || new Date(), true);
  };

  const [formData, setFormData] = useState({
    datetime: getInitialDatetime(),
    systolic: initialData.systolic?.toString() || '',
    diastolic: initialData.diastolic?.toString() || '',
    pulse: initialData.pulse?.toString() || '',
    notes: initialData.notes || '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    // Only update if adding new (not editing) and initialDate changes
    if (!initialData.date && initialDate) {
      setFormData(prev => ({
        ...prev,
        datetime: formatDateForInput(initialDate, true),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDate]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const systolic = parseInt(formData.systolic);
    const diastolic = parseInt(formData.diastolic);
    const pulse = formData.pulse ? parseInt(formData.pulse) : null;

    if (!formData.systolic) {
      newErrors.systolic = 'La presión sistólica es obligatoria';
    } else if (systolic < 50 || systolic > 300) {
      newErrors.systolic = 'Debe estar entre 50 y 300 mmHg';
    }

    if (!formData.diastolic) {
      newErrors.diastolic = 'La presión diastólica es obligatoria';
    } else if (diastolic < 30 || diastolic > 200) {
      newErrors.diastolic = 'Debe estar entre 30 y 200 mmHg';
    }

    if (systolic && diastolic && systolic <= diastolic) {
      newErrors.systolic = 'Debe ser mayor que la diastólica';
    }

    if (pulse && (pulse < 30 || pulse > 200)) {
      newErrors.pulse = 'Debe estar entre 30 y 200 ppm';
    }

    if (!formData.datetime) {
      newErrors.datetime = 'La fecha y hora son obligatorias';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Split datetime into separate date and time for backend
      const [datePart, timePart] = formData.datetime.split('T');

      const submitData: TensionFormData = {
        systolic: parseInt(formData.systolic),
        diastolic: parseInt(formData.diastolic),
        pulse: formData.pulse ? parseInt(formData.pulse) : undefined,
        notes: formData.notes || undefined,
        date: datePart,
        time: timePart,
      };

      await onSubmit(submitData);
    } catch (err: any) {
      setErrors({ submit: err.message || 'Error al guardar' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const systolic = parseInt(formData.systolic);
  const diastolic = parseInt(formData.diastolic);
  const showCategory = systolic && diastolic && systolic > diastolic;
  const category = showCategory ? getTensionCategory(systolic, diastolic) : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.submit && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-2 p-3.5 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/60 text-red-600 dark:text-red-400 text-sm"
        >
          <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
          <span>{errors.submit}</span>
        </motion.div>
      )}

      {/* Fecha y hora */}
      <div>
        <label className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text)] mb-2">
          <Clock size={15} className="text-[var(--color-accent)]" />
          Fecha y hora
        </label>
        <input
          type="datetime-local"
          value={formData.datetime}
          onChange={(e) => setFormData({ ...formData, datetime: e.target.value })}
          className={`input ${errors.datetime ? 'border-red-400' : ''}`}
          required
        />
        {errors.datetime && <p className="text-red-500 text-xs mt-1">{errors.datetime}</p>}
      </div>

      {/* Presión Arterial */}
      <div className="p-4 rounded-2xl bg-[var(--color-surface)] border border-[var(--color-border)]">
        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-4 flex items-center gap-2">
          <Heart size={18} className="text-red-500" />
          Presión Arterial
        </h3>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
              Sistólica (Alta) *
            </label>
            <div className="relative">
              <input
                type="number"
                value={formData.systolic}
                onChange={(e) => setFormData({ ...formData, systolic: e.target.value })}
                className={`input pr-16 ${errors.systolic ? 'border-red-400' : ''}`}
                placeholder="120"
                min="50"
                max="300"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-[var(--color-text-muted)]">
                mmHg
              </span>
            </div>
            {errors.systolic && <p className="text-red-500 text-xs mt-1">{errors.systolic}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
              Diastólica (Baja) *
            </label>
            <div className="relative">
              <input
                type="number"
                value={formData.diastolic}
                onChange={(e) => setFormData({ ...formData, diastolic: e.target.value })}
                className={`input pr-16 ${errors.diastolic ? 'border-red-400' : ''}`}
                placeholder="80"
                min="30"
                max="200"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-[var(--color-text-muted)]">
                mmHg
              </span>
            </div>
            {errors.diastolic && <p className="text-red-500 text-xs mt-1">{errors.diastolic}</p>}
          </div>
        </div>

        {/* Categoría de tensión */}
        {category && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-3 rounded-xl border-l-4"
            style={{ borderLeftColor: category.color, backgroundColor: `${category.color}10` }}
          >
            <p className="font-semibold text-sm" style={{ color: category.color }}>
              {category.category}
            </p>
            <p className="text-xs text-[var(--color-text-muted)]">{category.description}</p>
          </motion.div>
        )}
      </div>

      {/* Pulso */}
      <div>
        <label className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text)] mb-2">
          <Activity size={15} className="text-[var(--color-accent)]" />
          Pulso
          <span className="text-xs font-normal text-[var(--color-text-muted)]">· opcional</span>
        </label>
        <div className="relative">
          <input
            type="number"
            value={formData.pulse}
            onChange={(e) => setFormData({ ...formData, pulse: e.target.value })}
            className={`input pr-16 ${errors.pulse ? 'border-red-400' : ''}`}
            placeholder="70"
            min="30"
            max="200"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-[var(--color-text-muted)]">
            ppm
          </span>
        </div>
        {errors.pulse && <p className="text-red-500 text-xs mt-1">{errors.pulse}</p>}
      </div>

      {/* Notas */}
      <div>
        <label className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text)] mb-2">
          <FileText size={15} className="text-[var(--color-accent)]" />
          Notas
          <span className="text-xs font-normal text-[var(--color-text-muted)]">· opcional</span>
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          className="input min-h-[100px] resize-none"
          placeholder="Ejercicio, medicación, síntomas..."
          maxLength={500}
        />
        <p className="text-xs text-[var(--color-text-muted)] mt-1 text-right">
          {formData.notes.length}/500 caracteres
        </p>
      </div>

      {/* Botones */}
      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 py-4 rounded-xl border-2 border-[var(--color-border)] text-[var(--color-text)] font-medium hover:bg-[var(--color-surface)] transition-colors"
        >
          Cancelar
        </button>
        <motion.button
          type="submit"
          disabled={isSubmitting}
          whileTap={{ scale: 0.98 }}
          className="flex-1 btn btn-primary py-4"
        >
          {isSubmitting ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Save size={20} />
              Guardar
            </>
          )}
        </motion.button>
      </div>
    </form>
  );
};
